import '@splidejs/react-splide/css';
import {Splide, SplideSlide} from '@splidejs/react-splide';

import {styled, CTA, useTheme} from '@volkswagen-onehub/components-core';
import {ChevronLeft, ChevronRight} from '@volkswagen-onehub/icons-core';
import React, {useRef, useState} from 'react';
import {ModelImage} from '../../types/models';

interface ResultGalleryInterface {
  images: ModelImage[];
}

const ResultGalleryContainer = styled.div`
  flex: 1;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: ${(props) => props.theme.size.borderRadius.imageL};
  }

  .splide__slide {
    opacity: 0.5;
  }

  .splide__slide.is-active {
    transition: opacity 0.1s ease, transform 0.1s ease;
    opacity: 1;
  }
`;

const ArrowWrapper = styled.div`
  position: absolute;
  top: 50%;
  right: 0;
  left: 0;
  display: flex;
  justify-content: space-between;
  padding: 0 ${(props) => props.theme.size.static150};
  transform: translateY(-50%);
  pointer-events: none;

  button {
    pointer-events: initial;
  }
`;

const ArrowButton = styled.div<{$hidden?: boolean}>`
  visibility: ${(props) => (props.$hidden ? 'hidden' : 'visible')};
`;

const ResultGallery: React.FC<ResultGalleryInterface> = ({images}) => {
  const theme = useTheme();
  const splideRef = useRef<any>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrev = () => {
    if (splideRef.current) {
      splideRef.current.splide.go('<');
    }
  };

  const handleNext = () => {
    if (splideRef.current) {
      splideRef.current.splide.go('>');
    }
  };

  const handleMove = (_splide: any, newIndex: number) => {
    setCurrentIndex(newIndex);
  };

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === images.length - 1;

  return (
    <ResultGalleryContainer>
      <Splide
        ref={splideRef}
        aria-label="Vehicle Images"
        onMove={handleMove}
        options={{
          type: 'slide',
          mediaQuery: 'max',
          gap: theme.size.grid001,
          pagination: false,
          arrows: false,
          padding: {right: theme.size.grid002},
          breakpoints: {
            959: {
              padding: {left: theme.size.grid001, right: theme.size.grid002},
              gap: theme.size.grid001,
            },
          },
        }}
      >
        {images.map((image) => (
          <SplideSlide key={image.url}>
            <img src={image.url} alt={image.alt} />
          </SplideSlide>
        ))}
      </Splide>

      <ArrowWrapper>
        <ArrowButton $hidden={isFirst}>
          <CTA
            tag="button"
            emphasis="primary"
            onClick={handlePrev}
            aria-label="Previous image"
            icon={<ChevronLeft variant="small" aria-hidden="true" />}
          />
        </ArrowButton>
        <ArrowButton $hidden={isLast}>
          <CTA
            tag="button"
            emphasis="primary"
            onClick={handleNext}
            aria-label="Next image"
            icon={<ChevronRight variant="small" aria-hidden="true" />}
          />
        </ArrowButton>
      </ArrowWrapper>
    </ResultGalleryContainer>
  );
};

export default ResultGallery;
